import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { X, MapPin, Star, CheckCircle, XCircle, Download, ImageOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import PhotoLightbox from '@/components/shared/PhotoLightbox';
import { useState } from 'react';
import { orgTimezone, dateTimeInTz } from '@/features/staff/attendanceModel';
import { submitterLabel, isRenderableMediaUrl, fieldHeading } from './submissionDisplay';

function NotUploaded({ t }) {
  return (
    <span
      className="w-16 h-16 rounded-lg border border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 flex flex-col items-center justify-center gap-1 text-slate-400"
      title={t('forms.submissions.mediaNotUploadedHint', 'This file stayed on the device and was never uploaded.')}
    >
      <ImageOff size={16} />
      <span className="text-[9px] leading-tight text-center px-1">
        {t('forms.submissions.mediaNotUploaded', 'Not uploaded')}
      </span>
    </span>
  );
}

// formTemplate prop is optional: new code paths pass the form object
// directly (since useFormSubmissions returns flat rows without the join).
// Legacy paths (old inspection submissions) still have inspection_templates embedded.
export default function SubmissionDetail({ submission, formTemplate, onClose }) {
  const { t } = useTranslation();
  const [lightbox, setLightbox] = useState(null);
  const tz = orgTimezone(useSelector((s) => s.org?.settings));

  if (!submission) return null;

  const template = formTemplate || submission.inspection_templates;
  const fields = template?.fields || [];
  const answers = submission.answers || {};

  const renderAnswer = (field) => {
    const val = answers[field.id];

    switch (field.type) {
      case 'checkbox':
        return val ? (
          <span className="flex items-center gap-1 text-emerald-600"><CheckCircle size={14} /> {t('common.yes', 'Yes')}</span>
        ) : (
          <span className="flex items-center gap-1 text-red-500"><XCircle size={14} /> {t('common.no', 'No')}</span>
        );

      case 'rating': {
        const max = field.max || 5;
        return (
          <div className="flex gap-0.5">
            {Array.from({ length: max }, (_, i) => (
              <Star key={i} size={16} className={cn(i < (val || 0) ? 'fill-amber-400 text-amber-400' : 'text-slate-300')} />
            ))}
            <span className="text-xs text-slate-400 ms-1">({val || 0}/{max})</span>
          </div>
        );
      }

      case 'photo': {
        const photos = val || [];
        return (
          <div className="flex flex-wrap gap-2">
            {photos.map((url, i) => (
              isRenderableMediaUrl(url) ? (
                <img
                  key={i}
                  src={url}
                  alt=""
                  className="w-16 h-16 rounded-lg object-cover cursor-pointer border border-slate-200 dark:border-slate-700"
                  onClick={() => setLightbox(url)}
                />
              ) : (
                <NotUploaded key={i} t={t} />
              )
            ))}
            {photos.length === 0 && <span className="text-xs text-slate-400">—</span>}
          </div>
        );
      }

      case 'signature':
        if (!val) return <span className="text-xs text-slate-400">—</span>;
        return isRenderableMediaUrl(val) ? (
          <img src={val} alt="Signature" className="h-16 border border-slate-200 dark:border-slate-700 rounded-lg bg-white" />
        ) : (
          <NotUploaded t={t} />
        );

      case 'location':
        return val ? (
          <span className="flex items-center gap-1 text-xs text-sky-500">
            <MapPin size={12} />
            <bdi>{val.lat.toFixed(4)}°N, {val.lng.toFixed(4)}°E</bdi>
          </span>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        );

      case 'multi_select':
        return (val || []).length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {val.map((v) => (
              <span key={v} className="px-2 py-0.5 bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 rounded text-[11px]">{v}</span>
            ))}
          </div>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        );

      case 'file':
        if (!val) return <span className="text-xs text-slate-400">—</span>;
        return isRenderableMediaUrl(val) ? (
          <a href={val} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-sky-500 hover:underline">
            <Download size={12} /> {val.split('/').pop()}
          </a>
        ) : (
          <NotUploaded t={t} />
        );

      default:
        return <span className="text-sm">{val ?? '—'}</span>;
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
      <div className="fixed inset-y-0 end-0 z-50 w-full max-w-md bg-white dark:bg-slate-900 border-s border-slate-200 dark:border-slate-700 shadow-xl flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-sm">{template?.name}</h3>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {t('inspections.submittedBy', 'Submitted by')} <bdi>{submitterLabel(submission, t)}</bdi> · <bdi>{dateTimeInTz(submission.occurred_at || submission.created_at, tz)}</bdi>
            </p>
            {submission.work_id ? (
              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                {t('forms.submissions.attachedToWork', 'Attached to Work')}: {submission.work_id}
              </span>
            ) : submission.work_stop_id ? (
              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                {t('forms.submissions.attachedToStop', 'Attached to Stop')}: {submission.work_stop_id}
              </span>
            ) : submission?.dispatch_job_id ? (
              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                {t('forms.submissions.attachedToLegacyJob', 'Attached to Job (legacy)')}: {submission.dispatch_job_id}
              </span>
            ) : submission?.route_stop_id ? (
              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                {t('forms.submissions.attachedToLegacyRouteStop', 'Attached to Route stop (legacy)')}: {submission.route_stop_id}
              </span>
            ) : (
              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                {t('forms.submissions.freeFloating', 'Free-floating submission')}
              </span>
            )}
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>

        {/* Location meta */}
        {submission.location && (
          <div className="px-5 py-2 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 flex items-center gap-1.5 text-xs text-slate-500">
            <MapPin size={12} className="text-sky-500" />
            <bdi>{submission.location.lat.toFixed(4)}°N, {submission.location.lng.toFixed(4)}°E</bdi>
          </div>
        )}

        {/* Fields */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {fields.map((field) => (
            <div key={field.id}>
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                {fieldHeading(field)}
                {field.required && <span className="text-red-500 ms-0.5">*</span>}
              </div>
              {renderAnswer(field)}
            </div>
          ))}
        </div>
      </div>

      {lightbox && <PhotoLightbox src={lightbox} alt="Inspection photo" onClose={() => setLightbox(null)} />}
    </>
  );
}
